<script setup>
defineProps({
  to: {
    type: String,
    default: undefined,
  },
  target: {
    type: String,
    default: undefined,
  },
  href: {
    type: String,
    default: undefined,
  },
  download: {
    type: [Boolean, String],
    default: false,
  },
  theme: {
    type: String,
    default: 'white',
    validator(value) {
      return [
        'white',
        'white-outlined',
        'purple',
        'purple-outlined',
        'white-purple',
      ].includes(value);
    },
  },
  text: {
    type: String,
    default: undefined,
  },

  isStaticByDefault: {
    type: Boolean,
    default: false,
  },
  staticTag: {
    type: String,
    default: undefined,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  isSubmitted: {
    type: Boolean,
    default: false,
  },

  type: {
    type: String,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click']);

function emitClick() {
  emit('click');
}
</script>

<template>
  <CTagDetect
    class="ui-button"
    :class="[
      `ui-button--theme--${theme}`,
      { 'ui-button--submitted': isSubmitted },
    ]"
    :to="to"
    :href="href"
    :target="target"
    :type="type"
    :disabled="disabled || isLoading || isSubmitted"
    @click="emitClick"
  >
    <span class="ui-button__container">
      <slot name="icon-left" />

      <slot v-if="!isSubmitted">
        <span class="ui-button__font">
          {{ text }}
        </span>
      </slot>

      <slot name="icon-right" />

      <span v-show="isLoading" class="ui-button__loader" />

      <img
        v-if="isSubmitted"
        src="/icons/success.svg"
        alt=""
        class="ui-button__icon ui-button__icon--success"
      />
    </span>
  </CTagDetect>
</template>

<style scoped lang="scss">
.ui-button {
  $parent: &;

  display: flex;
  border-radius: em(8);
  transition: $time-2;
  transition-property: background-color, border-color, color, opacity;
  width: em(368);
  height: em(71);
  padding-block: em(15);
  padding-inline: em(25);
  text-align: center;

  @include media-breakpoint-mobile {
    width: em(308);
    height: em(56);
  }

  &:disabled {
    color: $color-white;
    pointer-events: none;
    background-color: $color-gray-1;
  }

  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  &__font {
    font-size: em(24);
    font-weight: $font-weight-medium;
    line-height: math.div(19, 24);

    @include media-breakpoint-mobile {
      font-size: em(17);
    }
  }

  &__container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  &__loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &::after {
      @include box(em(25));

      display: block;
      content: '';
      border: em(3) solid;
      border-color: $color-main-5 transparent $color-main-5 transparent;
      border-radius: 50%;
      animation: rotation 1.5s linear infinite;
    }
  }

  &__icon {
    &--success {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      @include box(em(34));
    }
  }

  &--theme {
    &--white {
      color: $color-black;
      background-color: $color-white;
    }

    &--white-outlined {
      color: $color-white;
      border: 1px solid $color-white;
    }

    &--purple {
      color: $color-white;
      background-color: $color-main-1;
    }

    &--purple-outlined {
      color: $color-main-1;
      border: 1px solid $color-main-1;
    }

    &--white-purple {
      color: $color-black;
      background-color: $color-white;

      @include media-breakpoint-mobile {
        color: $color-white;
        background-color: $color-main-1;
      }
    }
  }

  &--submitted {
    &:disabled {
      background-color: $color-green-4;
    }
  }
}
</style>
